import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collect } from 'collect.js';
import { Button, DatePicker, Dropdown, Space } from 'antd';
import { CalendarOutlined, CloseOutlined, DownOutlined } from '@ant-design/icons';
import CheckableTags from './CheckableTags';
import useScreenType from '../../hooks/utility/useScreenType';
import classnames from 'classnames';
import { DateTime } from 'luxon';

const { RangePicker } = DatePicker;

export const variant_dropdown = 'dropdown';
export const variant_radio_group = 'radio_group';

export const filter_today = 'today';
export const filter_yesterday = 'yesterday';
export const filter_this_week = 'this_week';
export const filter_last_week = 'last_week';
export const filter_this_month = 'this_month';
export const filter_last_month = 'last_month';
export const filter_all = 'all';

export const filterFactory = (filter) =>
  ({
    [filter_today]: {
      start_date: DateTime.local().startOf('day').toUTC(),
      end_date: DateTime.local().endOf('day').toUTC(),
    },
    [filter_yesterday]: {
      start_date: DateTime.local().minus({ days: 1 }).startOf('day').toUTC(),
      end_date: DateTime.local().minus({ days: 1 }).endOf('day').toUTC(),
    },
    [filter_this_week]: {
      start_date: DateTime.local().startOf('week').toUTC(),
      end_date: DateTime.local().endOf('week').toUTC(),
    },
    [filter_last_week]: {
      start_date: DateTime.local().minus({ weeks: 1 }).startOf('week').toUTC(),
      end_date: DateTime.local().minus({ weeks: 1 }).endOf('week').toUTC(),
    },
    [filter_this_month]: {
      start_date: DateTime.local().startOf('month').toUTC(),
      end_date: DateTime.local().endOf('month').toUTC(),
    },
    [filter_last_month]: {
      start_date: DateTime.local().minus({ months: 1 }).startOf('month').toUTC(),
      end_date: DateTime.local().minus({ months: 1 }).endOf('month').toUTC(),
    },
    [filter_all]: {
      start_date: null,
      end_date: null,
    },
  })[filter];

const _filters = [
  filter_today,
  filter_yesterday,
  filter_this_week,
  filter_last_week,
  filter_this_month,
  filter_last_month,
  filter_all,
];

export default function DateFilter({
  onChange,
  variant = variant_radio_group,
  defaultFilter = null,
  customFilters = [],
  excludeFilters = [],
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Lib:DateFilter' });

  const { isMobile } = useScreenType();

  const filters = useMemo(
    () =>
      collect(_filters)
        .map((item) => ({ id: item, label: t(`filters.${item}`), ...filterFactory(item) }))
        .merge(customFilters)
        .whereNotIn('id', excludeFilters),
    [customFilters, excludeFilters, t],
  );

  const [selectedFilter, setSelectedFilter] = useState(filters.firstWhere('id', defaultFilter) ?? filters.first());
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    if (dateRange) {
      const start = DateTime.fromJSDate(dateRange[0].toDate()).startOf('day');
      const end = DateTime.fromJSDate(dateRange[1].toDate()).endOf('day');

      onChange({
        start_date: start.startOf('day').toUTC(),
        end_date: (start.valueOf() === end.valueOf() ? end.endOf('day') : end).toUTC(),
      });

      return;
    }

    selectedFilter && onChange(selectedFilter);
  }, [selectedFilter, dateRange]);

  const onSelect = (filter) => {
    setSelectedFilter(filters.firstWhere('id', filter));
  };

  function toggleCustomFilter() {
    setShowDateRangePicker(false);
    setDateRange(null);
  }

  const renderVariant = {
    [variant_dropdown]: () => (
      <div className="flex gap-md">
        <Dropdown
          menu={{
            items: filters.map((filter) => ({ key: filter.id, label: filter.label })),
            selectable: true,
            selectedKeys: selectedFilter ? [selectedFilter.id] : null,
            onSelect: (e) => onSelect(e.key),
          }}
        >
          <Button>
            <Space>
              {selectedFilter?.label ?? 'Select period'}
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>
        <Button icon={<CalendarOutlined />} onClick={() => setShowDateRangePicker(true)} />
      </div>
    ),
    [variant_radio_group]: () => (
      <CheckableTags
        options={filters}
        selectedOption={selectedFilter}
        onChange={setSelectedFilter}
        extra={<Button icon={<CalendarOutlined />} onClick={() => setShowDateRangePicker(true)} />}
      />
    ),
  }[variant];

  return (
    <div className="flex items-center gap-md">
      {!showDateRangePicker && renderVariant()}
      {showDateRangePicker && (
        <>
          <RangePicker
            format="DD.MM.YYYY"
            value={dateRange}
            onChange={setDateRange}
            icon={<CalendarOutlined />}
            defaultOpen
            popupClassName={classnames({ 'single-calendar-mode': isMobile })}
          />
          <Button icon={<CloseOutlined />} onClick={toggleCustomFilter} />
        </>
      )}
    </div>
  );
}
